<template>
  <div class="culture-page">
    <CultureNav />
    <CultureHero
      title="Food and Drink Along the Way"
      summary="Restaurants, bars, breweries, and memorable meals that became part of the Reckon story."
      eyebrow="The Reckon Way"
    />
    <main>
      <v-container>
        <section class="eat-stats" aria-label="Eats archive summary">
          <article><strong>{{ eats.length }}</strong><span>Places Visited</span></article>
          <article><strong>{{ destinationCount }}</strong><span>Destinations</span></article>
          <article><strong>{{ storyCount }}</strong><span>Stories Told</span></article>
          <article><strong>{{ eats.length - storyCount }}</strong><span>Stories to Add</span></article>
        </section>

        <section class="filter-panel" aria-labelledby="eats-filter-heading">
          <div class="filter-heading">
            <p>Find a Table</p>
            <h2 id="eats-filter-heading">Explore the Collection</h2>
          </div>
          <label for="eats-search">
            Search
            <input id="eats-search" v-model.trim="searchQuery" type="search" placeholder="Restaurant or place">
          </label>
          <label for="eats-destination">
            Destination
            <select id="eats-destination" v-model="destinationFilter">
              <option value="">Everywhere</option>
              <option v-for="destination in destinations" :key="destination" :value="destination">
                {{ destination }}
              </option>
            </select>
          </label>
          <label for="eats-type">
            Type
            <select id="eats-type" v-model="typeFilter">
              <option value="">All types</option>
              <option v-for="type in types" :key="type" :value="type">{{ type }}</option>
            </select>
          </label>
          <label for="eats-story-status">
            Story Status
            <select id="eats-story-status" v-model="storyFilter">
              <option value="">All places</option>
              <option value="published">Stories told</option>
              <option value="needed">Stories to add</option>
            </select>
          </label>
        </section>

        <section class="results-section" aria-live="polite">
          <div class="results-heading">
            <div>
              <p>Collected Along the Way</p>
              <h2>{{ resultHeading }}</h2>
            </div>
            <span>{{ filteredEats.length }} {{ filteredEats.length === 1 ? 'place' : 'places' }}</span>
          </div>
          <div v-if="filteredEats.length" class="card-grid">
            <CultureCard
              v-for="eatery in filteredEats"
              :key="eatery.id"
              :title="eatery.name"
              :href="eatery.href"
              :image="eatery.image || fallbackImage"
              :eyebrow="eatery.type"
              :summary="eatery.description"
              :metadata="metadataFor(eatery)"
              :badge="eatery.hasNarrative ? 'Story Available' : ''"
              :cta-text="eatery.hasNarrative ? 'Read the Story' : 'Explore the Place'"
            />
          </div>
          <div v-else class="empty-state">
            <h3>No tables match those filters.</h3>
            <p>Try another destination, type, or story status.</p>
            <button type="button" @click="clearFilters">Clear filters</button>
          </div>
        </section>
      </v-container>
    </main>
    <AppFooter />
  </div>
</template>

<script>
import { mediaRef } from '@/services/mediaAssets';
import AppFooter from '@/components/Footer.vue';
import CultureNav from '@/components/reckon-way/culture/CultureNav.vue';
import CultureHero from '@/components/reckon-way/culture/CultureHero.vue';
import CultureCard from '@/components/reckon-way/culture/CultureCard.vue';
import { eats } from '@/content/culture/eats';

const fallbackImage = mediaRef('adventures/reckon-way-featured-fallback.png');

export default {
  name: 'CultureEatsView',
  components: {
    AppFooter, CultureNav, CultureHero, CultureCard,
  },
  data: () => ({
    eats,
    fallbackImage,
    searchQuery: '',
    destinationFilter: '',
    typeFilter: '',
    storyFilter: '',
  }),
  computed: {
    destinations() {
      return [...new Set(this.eats.flatMap((eatery) => eatery.destinations))].sort();
    },
    destinationCount() { return this.destinations.length; },
    types() { return [...new Set(this.eats.map((eatery) => eatery.type))].sort(); },
    storyCount() { return this.eats.filter((eatery) => eatery.hasNarrative).length; },
    filteredEats() {
      const query = this.searchQuery.toLowerCase();
      return this.eats.filter((eatery) => {
        const searchable = [
          eatery.name,
          eatery.description,
          ...eatery.locations,
          ...eatery.journeys,
        ].join(' ').toLowerCase();
        return (!query || searchable.includes(query))
          && (!this.destinationFilter || eatery.destinations.includes(this.destinationFilter))
          && (!this.typeFilter || eatery.type === this.typeFilter)
          && (!this.storyFilter
            || (this.storyFilter === 'published' && eatery.hasNarrative)
            || (this.storyFilter === 'needed' && !eatery.hasNarrative));
      });
    },
    resultHeading() {
      if (this.storyFilter === 'published') return 'Stories from the Table';
      if (this.storyFilter === 'needed') return 'Stories Still to Tell';
      if (this.destinationFilter) return `Eating Through ${this.destinationFilter}`;
      return 'Every Table, Bar, and Brewery';
    },
  },
  methods: {
    metadataFor(eatery) {
      const years = eatery.years.length > 2
        ? `${eatery.years.length} years`
        : eatery.years.join(' · ');
      return [
        eatery.destinations.join(' · '),
        eatery.michelinStars
          ? `${eatery.michelinStars} Michelin ${eatery.michelinStars === 1 ? 'Star' : 'Stars'}`
          : '',
        eatery.visitCount > 1 ? `${eatery.visitCount} visits` : '',
        years,
      ].filter(Boolean);
    },
    clearFilters() {
      this.searchQuery = '';
      this.destinationFilter = '';
      this.typeFilter = '';
      this.storyFilter = '';
    },
  },
};
</script>

<style scoped>
.culture-page, main { background: #050505; }
.culture-page main { padding: 48px 0 76px; }
.eat-stats { display: grid; gap: 12px; grid-template-columns: repeat(4, minmax(0, 1fr)); }
.eat-stats article { background: linear-gradient(145deg, #18120b, #101010); border: 1px solid #3b3020; border-radius: 12px; padding: 24px; }
.eat-stats strong { color: #f0b13c; display: block; font-family: "Oswald", sans-serif; font-size: 2.4rem; line-height: 1; }
.eat-stats span { color: #bdbdbd; display: block; font-size: .74rem; font-weight: 800; margin-top: 8px; text-transform: uppercase; }
.filter-panel { align-items: end; background: #111; border: 1px solid #303030; border-radius: 12px; display: grid; gap: 16px; grid-template-columns: 1.25fr repeat(4, minmax(140px, 1fr)); margin-top: 28px; padding: 24px; }
.filter-heading p, .results-heading p { color: #e23232; font-family: "Oswald", sans-serif; font-size: .78rem; font-weight: 900; margin: 0 0 5px; text-transform: uppercase; }
.filter-heading h2, .results-heading h2 { color: #fff; font-family: "Oswald", sans-serif; font-size: clamp(1.8rem, 4vw, 2.5rem); line-height: 1; margin: 0; }
.filter-panel label { color: #aaa; font-size: .7rem; font-weight: 800; text-transform: uppercase; }
.filter-panel input, .filter-panel select { background: #1b1b1b; border: 1px solid #424242; border-radius: 6px; color: #fff; display: block; margin-top: 7px; min-height: 44px; padding: 10px; width: 100%; }
.results-section { border-top: 1px solid #292929; margin-top: 54px; padding-top: 48px; }
.results-heading { align-items: end; display: flex; justify-content: space-between; margin-bottom: 26px; }
.results-heading > span { border: 1px solid #3a3a3a; border-radius: 999px; color: #bbb; font-size: .78rem; padding: 7px 11px; }
.card-grid { display: grid; gap: 16px; grid-template-columns: repeat(auto-fit, minmax(270px, 1fr)); }
.empty-state { background: #111; border: 1px solid #303030; border-radius: 12px; color: #aaa; padding: 44px; text-align: center; }
.empty-state h3 { color: #fff; font-family: "Oswald", sans-serif; font-size: 1.8rem; margin: 0; }
.empty-state p { margin: 8px 0 20px; }
.empty-state button { background: #e23232; border: 0; border-radius: 999px; color: #fff; cursor: pointer; font-weight: 900; padding: 10px 18px; text-transform: uppercase; }
@media (max-width: 1050px) { .filter-panel { grid-template-columns: repeat(2, minmax(0, 1fr)); } .filter-heading { grid-column: 1 / -1; } }
@media (max-width: 760px) { .eat-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 540px) { .filter-panel { grid-template-columns: 1fr; } .filter-heading { grid-column: auto; } .results-heading { align-items: flex-start; flex-direction: column; gap: 14px; } }
</style>
